/**
 * Club Olay + OrderGroove Integration
 * 
 * This script ensures proper coordination between Club Olay member pricing
 * and OrderGroove subscription discounts.
 * 
 * Order of discounts:
 * 1. Club Olay member discount (applied via Cart Transform)
 * 2. OrderGroove subscription discount (applied on top of Club Olay price)
 * 
 * @version 1.0.0
 */

(function() {
  'use strict';

  const ClubOlayOrdergrooveIntegration = {
    
    /**
     * Initialize the integration
     */
    init() {
      // Wait for both Club Olay Pricing and OrderGroove to be ready
      this.waitForDependencies().then(() => {
        this.setupIntegration();
      });
    },

    /**
     * Wait for required dependencies to load
     * @returns {Promise}
     */
    waitForDependencies() {
      return new Promise((resolve) => {
        const checkDependencies = () => {
          const clubOlayReady = typeof window.ClubOlayPricing !== 'undefined';
          const ordergrooveReady = typeof window.OG !== 'undefined' || 
                                   typeof window.ordergroove !== 'undefined';
          
          if (clubOlayReady && ordergrooveReady) {
            resolve();
          } else {
            setTimeout(checkDependencies, 100);
          }
        };
        
        checkDependencies();
        
        // Timeout after 10 seconds
        setTimeout(() => {
          resolve();
        }, 10000);
      });
    },

    /**
     * Setup the integration between Club Olay and OrderGroove
     */
    setupIntegration() {
      
      // Override OrderGroove price display if needed
      this.setupPriceDisplay();
      
      // Listen for OrderGroove events
      this.bindOrderGrooveEvents();
      
      // Update prices when subscription is toggled
      this.updatePricesOnSubscriptionToggle();
    },

    /**
     * Setup price display coordination
     */
    setupPriceDisplay() {
      // OrderGroove will automatically see the Club Olay transformed price
      // as the base price due to Cart Transform function
      
      // Add a note on the product page about stacking discounts
      this.addDiscountStackingNotice();
    },

    /**
     * Add a notice about discount stacking for Club Olay members with subscriptions
     */
    addDiscountStackingNotice() {
      const isClubMember = document.body.dataset.clubOlayMember === 'true';
      
      if (!isClubMember) return;
      
      // Find OrderGroove subscription widgets
      const ogWidgets = document.querySelectorAll('[data-ordergroove-product], .ordergroove-subscription-widget');
      
      ogWidgets.forEach(widget => {
        // Check if notice already exists
        if (widget.querySelector('.club-olay-og-notice')) return;
        
        // Create notice element
        const notice = document.createElement('div');
        notice.className = 'club-olay-og-notice';
        notice.innerHTML = `
          <div class="club-olay-og-notice__content">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M8 0L10.1 5.5L16 6.2L11.8 10.1L13 16L8 13.1L3 16L4.2 10.1L0 6.2L5.9 5.5L8 0Z" fill="currentColor"/>
            </svg>
            <span>Your Club Olay member discount will be applied first, then your subscription savings!</span>
          </div>
        `;
        
        // Add styles
        const style = document.createElement('style');
        style.textContent = `
          .club-olay-og-notice {
            margin: 0.75rem 0;
            padding: 0.625rem 0.875rem;
            background: linear-gradient(135deg, #fff9e6 0%, #fff5d6 100%);
            border-left: 3px solid #d4af37;
            border-radius: 0.25rem;
          }
          
          .club-olay-og-notice__content {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.8125rem;
            color: #4a4a4a;
            line-height: 1.4;
          }
          
          .club-olay-og-notice__content svg {
            flex-shrink: 0;
            width: 16px;
            height: 16px;
            color: #d4af37;
          }
        `;
        
        // Insert notice after the widget
        widget.parentNode.insertBefore(notice, widget.nextSibling);
        document.head.appendChild(style);
      });
    },

    /**
     * Bind OrderGroove event listeners
     */
    bindOrderGrooveEvents() {
      // Listen for OrderGroove subscription toggle events
      document.addEventListener('click', (e) => {
        const subscriptionToggle = e.target.closest('[data-ordergroove-toggle], .ordergroove-toggle');
        
        if (subscriptionToggle) {
          setTimeout(() => {
            this.handleSubscriptionToggle();
          }, 100);
        }
      });
      
      // Listen for OrderGroove's custom events if available
      if (window.OG) {
        try {
          window.OG.on('subscription:toggle', () => {
            this.handleSubscriptionToggle();
          });
        } catch (error) {
          // Silent error
        }
      }
    },

    /**
     * Handle subscription toggle
     */
    handleSubscriptionToggle() {
      // Dispatch custom event
      const event = new CustomEvent('ordergroove:subscription:toggle', {
        detail: {
          timestamp: Date.now()
        }
      });
      document.dispatchEvent(event);
      
      // Update price displays
      if (window.ClubOlayPricing && window.ClubOlayPricing.refreshPricing) {
        window.ClubOlayPricing.refreshPricing();
      }
    },

    /**
     * Update prices when subscription is toggled
     */
    updatePricesOnSubscriptionToggle() {
      document.addEventListener('ordergroove:subscription:toggle', (e) => {
        // The Cart Transform function will handle the actual price updates
        // This is just for visual feedback and logging
        
        // Update any dynamic price displays
        this.updateDynamicPrices();
      });
    },

    /**
     * Update dynamic price displays
     */
    updateDynamicPrices() {
      const priceElements = document.querySelectorAll('[data-price-display]');
      
      priceElements.forEach(element => {
        // Add a loading state briefly
        element.style.opacity = '0.6';
        
        setTimeout(() => {
          element.style.opacity = '1';
        }, 200);
      });
    },

    /**
     * Get effective price considering both Club Olay and OrderGroove discounts
     * @param {number} basePrice - Original product price in cents
     * @param {string} clubDiscountType - 'percentage' or 'fixed'
     * @param {number} clubDiscountValue - Club Olay discount value
     * @param {number} ogDiscountPercent - OrderGroove subscription discount percentage
     * @returns {Object}
     */
    calculateStackedPrice(basePrice, clubDiscountType, clubDiscountValue, ogDiscountPercent = 0) {
      // Step 1: Apply Club Olay discount
      let clubPrice = basePrice;
      let clubSavings = 0;
      
      if (clubDiscountType === 'percentage') {
        clubSavings = Math.round(basePrice * (clubDiscountValue / 100));
        clubPrice = basePrice - clubSavings;
      } else if (clubDiscountType === 'fixed') {
        clubSavings = Math.round(clubDiscountValue * 100); // Convert to cents
        clubPrice = Math.max(0, basePrice - clubSavings);
      }
      
      // Step 2: Apply OrderGroove discount on top of Club Olay price
      let finalPrice = clubPrice;
      let ogSavings = 0;
      
      if (ogDiscountPercent > 0) {
        ogSavings = Math.round(clubPrice * (ogDiscountPercent / 100));
        finalPrice = clubPrice - ogSavings;
      }
      
      const totalSavings = basePrice - finalPrice;
      const totalSavingsPercent = basePrice > 0 ? Math.round((totalSavings / basePrice) * 100) : 0;
      
      return {
        basePrice: basePrice,
        clubOlayPrice: clubPrice,
        clubOlaySavings: clubSavings,
        ordergrooveSavings: ogSavings,
        finalPrice: Math.max(0, finalPrice),
        totalSavings: totalSavings,
        totalSavingsPercent: totalSavingsPercent
      };
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ClubOlayOrdergrooveIntegration.init());
  } else {
    ClubOlayOrdergrooveIntegration.init();
  }

  // Expose for external access
  window.ClubOlayOrdergrooveIntegration = ClubOlayOrdergrooveIntegration;

})();
